// Phase 9.3.B — Portal brand assets gallery (2026-05-12).
//
// Spec: docs/apps/client-portal.md § "Brand Asset Library".
//
// Grid view of the client's brand assets — logos, patterns, typography,
// brand guides, mood boards. Filter chips by tag. Upload + delete in
// place. Images render via the unified `/portal/files/:id/download`
// presigned URL endpoint; non-image files (PDFs, etc.) show a filename
// tile with download link.
import { useCallback, useRef, useState } from "react";
import { ApiError } from "@/lib/api";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type BrandAssetTag,
  type PortalBrandAsset,
  TAG_LABEL,
  TAG_PILL_CLASS,
  deletePortalBrandAsset,
  fetchPortalBrandAssets,
  fetchPortalFileDownloadUrl,
  formatBytes,
  formatDate,
  isImageMime,
  uploadPortalBrandAsset,
} from "@/lib/portal-brand-assets";
import { useAuth } from "@/auth/AuthProvider";
import { PortalLayout } from "@/components/PortalLayout";

const ALL_TAGS: BrandAssetTag[] = [
  "logo",
  "pattern",
  "typography",
  "brand_guide",
  "mood_board",
  "other",
];

export function BrandAssetsPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [tagFilter, setTagFilter] = useState<BrandAssetTag | null>(null);
  const [pendingTag, setPendingTag] = useState<BrandAssetTag>("logo");
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["portal-brand-assets", tagFilter],
    queryFn: () =>
      fetchPortalBrandAssets({
        tag: tagFilter ? [tagFilter] : undefined,
        limit: 100,
      }),
  });

  const uploadMutation = useMutation({
    mutationFn: uploadPortalBrandAsset,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["portal-brand-assets"] });
      setError(null);
    },
    onError: (err) => {
      // A refused file type comes back with a plain-words message (M3).
      const msg = err instanceof ApiError ? (err.body as { message?: string } | null)?.message : undefined;
      setError(
        msg ?? (err instanceof Error
          ? err.message
          : "Upload failed. Try again."),
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deletePortalBrandAsset,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["portal-brand-assets"] });
    },
    onError: (err) => {
      setError(
        err instanceof Error
          ? err.message
          : "Couldn't delete this asset. Try again.",
      );
    },
  });

  const onFileSelected = useCallback(
    (file: File) => {
      setError(null);
      uploadMutation.mutate({ file, tag: pendingTag });
    },
    [pendingTag, uploadMutation],
  );

  function triggerFilePicker(): void {
    fileInputRef.current?.click();
  }

  function onInputChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    if (file) onFileSelected(file);
    // Clear the input so the same file can be re-selected later.
    e.target.value = "";
  }

  if (!user) return null;
  const assets = data?.rows ?? [];

  return (
    <PortalLayout>
        <div className="mb-10">
          <Link
            to="/dashboard"
            className="mb-3 inline-block text-sm font-semibold text-ink underline-offset-2 hover:underline"
          >
            ← Dashboard
          </Link>
          <h1 className="font-display text-4xl font-bold tracking-tight">
            Brand assets
          </h1>
          <p className="mt-2 text-brown-400">
            Your logos, patterns, and brand guides — kept in one place so
            we always have the latest.
          </p>
        </div>

        {/* Upload card */}
        <section className="mb-8 rounded-lg border border-peach-300 bg-peach-50 p-6 shadow-sm">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h2 className="mb-1 font-display text-lg font-bold tracking-tight">
                Upload an asset
              </h2>
              <p className="text-sm text-brown-500">
                PDF, PNG, SVG, JPG, or design file up to 50 MB.
              </p>
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <label className="block">
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-brown-300">
                  Tag
                </span>
                <select
                  value={pendingTag}
                  onChange={(e) =>
                    setPendingTag(e.target.value as BrandAssetTag)
                  }
                  className="rounded-md border border-cream-400 bg-white px-3 py-2 text-sm font-semibold text-brown-500 focus:border-peach-400 focus:outline-none"
                >
                  {ALL_TAGS.map((t) => (
                    <option key={t} value={t}>
                      {TAG_LABEL[t]}
                    </option>
                  ))}
                </select>
              </label>
              <input
                ref={fileInputRef}
                type="file"
                onChange={onInputChange}
                className="hidden"
              />
              <button
                type="button"
                onClick={triggerFilePicker}
                disabled={uploadMutation.isPending}
                className="rounded-md bg-ink px-6 py-2.5 font-semibold text-white shadow-sm transition-colors hover:bg-ink-700 disabled:opacity-50"
              >
                {uploadMutation.isPending ? "Uploading…" : "Choose file"}
              </button>
            </div>
          </div>
          {error && (
            <div className="mt-4 rounded-md border border-rust-200 bg-rust-50 px-4 py-3 text-sm text-rust-500">
              {error}
            </div>
          )}
        </section>

        {/* Filter chips */}
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <span className="text-xs font-semibold uppercase tracking-wider text-brown-400">
            Filter
          </span>
          <button
            type="button"
            onClick={() => setTagFilter(null)}
            className={
              "rounded-full border px-3 py-1 text-xs font-semibold transition-colors " +
              (tagFilter === null
                ? "border-rust-400 bg-ink text-white"
                : "border-cream-300 bg-white text-brown-400 hover:border-brown-300 hover:text-brown-500")
            }
          >
            All
          </button>
          {ALL_TAGS.map((t) => {
            const active = tagFilter === t;
            return (
              <button
                key={t}
                type="button"
                onClick={() => setTagFilter(active ? null : t)}
                className={
                  "rounded-full border px-3 py-1 text-xs font-semibold transition-colors " +
                  (active
                    ? "border-rust-400 bg-ink text-white"
                    : "border-cream-300 bg-white text-brown-400 hover:border-brown-300 hover:text-brown-500")
                }
              >
                {TAG_LABEL[t]}
              </button>
            );
          })}
        </div>

        {/* Grid */}
        <div className="rounded-lg border border-cream-200 bg-white p-6 shadow-sm">
          {isLoading && (
            <div className="px-6 py-12 text-center text-brown-300">
              Loading your assets…
            </div>
          )}
          {isError && (
            <div className="px-6 py-12 text-center">
              <p className="mb-3 text-rust-500">We couldn't load your assets.</p>
              <button
                type="button"
                onClick={() => void refetch()}
                className="text-sm font-semibold text-ink underline-offset-2 hover:underline"
              >
                Try again
              </button>
            </div>
          )}
          {!isLoading && !isError && assets.length === 0 && (
            <div className="px-6 py-12 text-center">
              <p className="mb-2 font-display text-2xl font-bold tracking-tight text-brown-500">
                {tagFilter
                  ? `No ${TAG_LABEL[tagFilter].toLowerCase()} yet`
                  : "No brand assets yet"}
              </p>
              <p className="text-brown-400">
                Upload your first one above.
              </p>
            </div>
          )}
          {assets.length > 0 && (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
              {assets.map((a) => (
                <BrandAssetTile
                  key={a.id}
                  asset={a}
                  onDelete={() => deleteMutation.mutate(a.id)}
                  deleting={deleteMutation.isPending}
                />
              ))}
            </div>
          )}
        </div>
    </PortalLayout>
  );
}

interface TileProps {
  asset: PortalBrandAsset;
  onDelete: () => void;
  deleting: boolean;
}

function BrandAssetTile({ asset, onDelete, deleting }: TileProps) {
  const isImage = isImageMime(asset.mimeType);
  const { data: download } = useQuery({
    queryKey: ["portal-file-download", asset.fileId],
    queryFn: () => fetchPortalFileDownloadUrl(asset.fileId),
    // Only fetch the URL when we'll actually use it (for image preview).
    enabled: isImage,
    // Presigned URL expires in 10 min; refetch every 8 min so an open
    // gallery doesn't go stale during a long browsing session.
    staleTime: 8 * 60 * 1000,
  });

  return (
    <div className="overflow-hidden rounded-lg border border-cream-200 bg-white shadow-sm">
      <div className="flex h-40 items-center justify-center bg-cream-100">
        {isImage && download?.url ? (
          <img
            src={download.url}
            alt={asset.originalFilename}
            className="h-full w-full object-contain"
          />
        ) : (
          <div className="px-4 text-center">
            <div className="font-display text-xs font-bold uppercase tracking-wider text-brown-300">
              {asset.mimeType.split("/")[1] ?? "File"}
            </div>
            <div className="mt-2 text-xs text-brown-400">
              {formatBytes(asset.sizeBytes)}
            </div>
          </div>
        )}
      </div>
      <div className="p-3">
        <div className="mb-2">
          <span
            className={
              "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold " +
              TAG_PILL_CLASS[asset.tag]
            }
          >
            {TAG_LABEL[asset.tag]}
          </span>
        </div>
        <div
          className="mb-1 truncate text-sm text-brown-500"
          title={asset.originalFilename}
        >
          {asset.originalFilename}
        </div>
        <div className="mb-3 text-xs text-brown-300">
          {formatDate(asset.uploadedAt)} · {formatBytes(asset.sizeBytes)}
        </div>
        <div className="flex justify-between text-xs">
          {download?.url ? (
            <a
              href={download.url}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-ink underline-offset-2 hover:underline"
            >
              Download
            </a>
          ) : (
            <DownloadLazyButton fileId={asset.fileId} />
          )}
          <button
            type="button"
            onClick={onDelete}
            disabled={deleting}
            className="font-semibold text-brown-400 underline-offset-2 hover:text-rust-500 hover:underline disabled:opacity-50"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Non-image files: fetch the download URL on-click only — avoids minting
 * a presigned URL for every PDF in the gallery on page load.
 */
function DownloadLazyButton({ fileId }: { fileId: string }) {
  const [loading, setLoading] = useState(false);
  async function onClick(): Promise<void> {
    setLoading(true);
    try {
      const res = await fetchPortalFileDownloadUrl(fileId);
      window.open(res.url, "_blank", "noopener,noreferrer");
    } finally {
      setLoading(false);
    }
  }
  return (
    <button
      type="button"
      onClick={() => void onClick()}
      disabled={loading}
      className="font-semibold text-ink underline-offset-2 hover:underline disabled:opacity-50"
    >
      {loading ? "Opening…" : "Download"}
    </button>
  );
}
